import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Building2, Check, ChevronsUpDown, Plus } from "@/components/ds/icons";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { RutInput } from "@/components/RutInput";
import { formatRut, validateRut } from "@/lib/rut";
import { inicialesEmpresa } from "@/lib/empresa-marca";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type View = "list" | "create";

/**
 * Selector de empresa en la barra superior. Reemplaza el título estático del
 * header por la empresa activa; al abrirlo muestra todas las empresas a las que
 * el usuario tiene acceso, permite cambiar entre ellas sin re-login y crear una
 * empresa nueva (el usuario queda como owner).
 *
 * El cambio no golpea al backend: cada empresa ya tiene su token en
 * `auth_sessions` (localStorage). Crear empresa sí (POST /auth/web/empresas): el
 * backend devuelve la sesión nueva, que el contexto agrega y deja activa.
 */
export function EmpresaSwitcher() {
  const { user, sessions, activeEmpresaId, switchEmpresa, createEmpresa } = useUser();
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("list");
  const [switching, setSwitching] = useState(false);
  // URL de logo que el navegador no pudo cargar (archivo faltante, APP_URL mal
  // configurada en el backend, red caída). Guardamos la URL y no un booleano:
  // al subir un logo nuevo la URL cambia (lleva sufijo aleatorio), así el
  // fallback se limpia solo sin necesidad de un efecto.
  const [logoRoto, setLogoRoto] = useState<string | null>(null);

  // Form de creación
  const [empresaRut, setEmpresaRut] = useState("");
  const [empresaNombre, setEmpresaNombre] = useState("");
  // Pre-marcado: reutilizar el correo del usuario como los 3 emails de la
  // empresa nueva (contacto, registros, vencimientos) evita re-tipearlos.
  const [usarEmailUsuario, setUsarEmailUsuario] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showRutError, setShowRutError] = useState(false);

  // Excluimos la sesión sintética de super-admin (empresa === null).
  const empresas = sessions.filter((s) => s.empresa);

  // Sin empresas (p. ej. super-admin): conservamos el título original para no
  // dejar el header vacío.
  if (empresas.length === 0) {
    return <span className="font-medium text-sm">Sistema de Gestión Documental</span>;
  }

  const activeNombre =
    user?.empresa?.nombre ??
    empresas.find((s) => s.empresa?.id === activeEmpresaId)?.empresa?.nombre ??
    "Empresa";

  const activeLogoUrl = user?.empresa?.logo_url ?? null;
  // Si el logo no carga, caemos al ícono genérico en vez de dejar un hueco.
  const mostrarLogo = activeLogoUrl !== null && logoRoto !== activeLogoUrl;

  const resetForm = () => {
    setEmpresaRut("");
    setEmpresaNombre("");
    setUsarEmailUsuario(true);
    setShowRutError(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Al cerrar, volvemos siempre a la lista y limpiamos el form.
    if (!next) {
      setView("list");
      resetForm();
    }
  };

  const handleSelect = async (empresaId: number, nombre: string) => {
    if (empresaId === activeEmpresaId) {
      handleOpenChange(false);
      return;
    }
    setSwitching(true);
    try {
      await switchEmpresa(empresaId);
      toast.success(`Empresa activa: ${nombre}`);
      handleOpenChange(false);
      // El token de la pestaña cambió; volvemos al dashboard para que las
      // vistas recarguen con la empresa nueva y no muestren datos de la anterior.
      setLocation("/");
    } catch {
      toast.error("No se pudo cambiar de empresa. Intenta nuevamente.");
    } finally {
      setSwitching(false);
    }
  };

  const rutValido = validateRut(empresaRut);
  const nombreValido = empresaNombre.trim().length > 0;
  const formValido = rutValido && nombreValido;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValido) {
      setShowRutError(true);
      return;
    }
    setSubmitting(true);
    try {
      await createEmpresa(empresaRut, empresaNombre.trim(), usarEmailUsuario);
      toast.success(`Empresa creada: ${empresaNombre.trim()}`);
      handleOpenChange(false);
      // La empresa nueva ya quedó activa en esta pestaña; al dashboard.
      setLocation("/");
    } catch {
      toast.error("No se pudo crear la empresa. Revisa los datos e intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {/* Selector del DS: el contexto multi-empresa siempre visible y
            etiquetado — logo 28px + "EMPRESA ACTIVA" + nombre. */}
        <button
          className="flex h-10 max-w-[17rem] cursor-pointer items-center gap-2.5 rounded-xl border bg-background py-0 pr-2.5 pl-[5px] transition-colors hover:bg-border-faint"
          aria-label="Cambiar de empresa"
        >
          {mostrarLogo ? (
            <img
              src={activeLogoUrl}
              alt=""
              onError={() => setLogoRoto(activeLogoUrl)}
              className="size-7 shrink-0 rounded-md object-contain"
            />
          ) : (
            /* Sin logo van las iniciales, no un ícono genérico: distinguen una
               empresa de otra al cambiar de contexto, y son exactamente lo que
               el preview del onboarding le prometió al usuario. */
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-[11.5px] font-semibold text-primary-foreground">
              {inicialesEmpresa(activeNombre)}
            </span>
          )}
          <span className="flex min-w-0 flex-col items-start leading-[1.25]">
            <span className="text-[9.5px] font-semibold tracking-[0.07em] text-soft">
              EMPRESA ACTIVA
            </span>
            <span className="max-w-[11rem] truncate text-[13px] font-semibold tracking-[-0.01em]">
              {activeNombre}
            </span>
          </span>
          <ChevronsUpDown className="size-[18px] shrink-0 text-soft" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {view === "list" ? (
          <>
            <DialogHeader>
              <DialogTitle>Cambiar de empresa</DialogTitle>
              <DialogDescription>
                Selecciona la empresa con la que quieres trabajar.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
            <div className="flex flex-col gap-1">
              {empresas.map((s) => {
                const empresa = s.empresa!;
                const isActive = empresa.id === activeEmpresaId;
                return (
                  <button
                    key={empresa.id}
                    onClick={() => handleSelect(empresa.id, empresa.nombre)}
                    disabled={switching}
                    className={cn(
                      // Patrón único de selección del DS: borde teal + fondo
                      // #F5FCFA + texto #0A6B60.
                      "flex items-center gap-3 rounded-[9px] border px-3 py-2.5 text-left transition-colors",
                      isActive
                        ? "border-primary bg-primary-50 text-primary-strong"
                        : "border-transparent hover:bg-border-faint",
                      switching && "pointer-events-none opacity-60",
                    )}
                  >
                    <div
                      className={cn(
                        "flex size-9 shrink-0 items-center justify-center rounded-lg",
                        isActive ? "bg-primary-100 text-primary-hover" : "bg-muted text-soft",
                      )}
                    >
                      <Building2 className="size-4" />
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[13px] font-medium">{empresa.nombre}</span>
                      {empresa.rut && (
                        <span className="fc-num truncate text-[11.5px] text-soft">
                          {formatRut(empresa.rut)}
                        </span>
                      )}
                    </div>
                    {isActive && <Check className="ml-auto size-4 shrink-0 text-primary" />}
                  </button>
                );
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => setView("create")}
            >
              <Plus className="h-4 w-4" />
              Crear nueva empresa
            </Button>
            </DialogBody>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground cursor-pointer"
                  aria-label="Volver a la lista"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <DialogTitle>Crear nueva empresa</DialogTitle>
              </div>
              <DialogDescription>
                Vas a quedar como dueño. Puedes completar la razón social y el resto de los datos
                después, en Mi Empresa.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:gap-[18px] md:p-[18px]">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="empresa-rut">RUT de la empresa</Label>
                <RutInput
                  id="empresa-rut"
                  value={empresaRut}
                  onChange={setEmpresaRut}
                  validate
                  showError={showRutError}
                  placeholder="12.345.678-9"
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="empresa-nombre">Nombre de la empresa</Label>
                <Input
                  id="empresa-nombre"
                  value={empresaNombre}
                  onChange={(e) => setEmpresaNombre(e.target.value)}
                  placeholder="Nombre de fantasía"
                  maxLength={255}
                  disabled={submitting}
                />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="usar-email-usuario"
                  checked={usarEmailUsuario}
                  onCheckedChange={(checked) => setUsarEmailUsuario(checked === true)}
                  disabled={submitting}
                  className="mt-0.5"
                />
                {/* `block` por lo mismo que en el consentimiento del registro:
                    el Label del DS es flex, y con el correo interpolado la frase
                    queda partida en columnas en vez de envolver como texto. */}
                <Label
                  htmlFor="usar-email-usuario"
                  className="block text-xs font-normal text-muted-foreground leading-snug"
                >
                  Usar mi correo{user?.email ? ` (${user.email})` : ""} como Email de Contacto,
                  Email para Registros y Email para Vencimientos de la empresa nueva.
                </Label>
              </div>
              <Button type="submit" className="w-full" disabled={submitting || !formValido}>
                {submitting ? "Creando…" : "Crear empresa"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
